import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { RiUserFill, RiUserLine } from 'react-icons/ri'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import type { IQuestionBankEntryDetail } from '@brushfire/types'
import { questionBankSaveBookmarkButtonSx } from '@/shared/lib/libraryCardHover'
import type { BuilderQuestion } from './types'
import type { ProfileLink } from './profile-attributes.api'
import {
  ProfileUpdateConfirmSection,
  ProfileUpdateEditSection,
  ProfileUpdateSimilarSection,
} from './ProfileUpdateQuestionDialogSections'
import { profileUpdateDialogTestId, useProfileUpdateQuestionFlow } from './profileUpdateQuestionFlow'

type Props = Readonly<{
  question: BuilderQuestion
  onChange: (patch: Partial<BuilderQuestion>) => void
  onUseBankEntry: (entry: IQuestionBankEntryDetail, link: ProfileLink) => void
}>

function runAsync(handler: () => Promise<void>): void {
  Promise.resolve(handler()).catch(() => undefined)
}

export function ProfileUpdateQuestionButton({ question, onChange, onUseBankEntry }: Props) {
  const flow = useProfileUpdateQuestionFlow({ question, onChange, onUseBankEntry })
  const {
    active,
    profileAllowed,
    mode,
    busy,
    error,
    match,
    name,
    setName,
    freshnessTag,
    setFreshnessTag,
    open,
    close,
    cancelConfirm,
    continueConfirm,
    applySimilarMatch,
    saveUpdate,
  } = flow

  const button = (
    <IconButton
      data-testid="question-profile-update"
      aria-label="Profile update question"
      aria-pressed={active}
      disabled={!profileAllowed}
      onClick={open}
      sx={{
        ...questionBankSaveBookmarkButtonSx,
        color: active ? '#008cd0' : '#64748b',
        ...(active ? { borderColor: '#008cd0' } : {}),
      }}
    >
      {active ? (
        <RiUserFill size={18} aria-hidden data-testid="question-profile-update-filled" />
      ) : (
        <RiUserLine size={18} aria-hidden data-testid="question-profile-update-outline" />
      )}
    </IconButton>
  )

  const dialogMaxWidth = mode === 'confirm' ? 646 : 448

  return (
    <>
      {profileAllowed ? (
        button
      ) : (
        <Tooltip title="Profile updates are available for choice and short-text questions.">
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {button}
          </Box>
        </Tooltip>
      )}

      <Dialog
        open={mode !== null}
        onClose={mode === 'confirm' ? cancelConfirm : close}
        data-testid={profileUpdateDialogTestId(mode)}
        PaperProps={{
          elevation: 0,
          sx: {
            width: '100%',
            maxWidth: dialogMaxWidth,
            borderRadius: '10px',
            boxShadow: '0px 20px 12.5px rgba(0, 0, 0, 0.1), 0px 8px 5px rgba(0, 0, 0, 0.1)',
            m: 2,
            maxHeight: 'calc(100vh - 32px)',
          },
        }}
      >
        {mode === 'confirm' ? (
          <ProfileUpdateConfirmSection
            name={name}
            setName={setName}
            freshnessTag={freshnessTag}
            setFreshnessTag={setFreshnessTag}
            error={error}
            busy={busy}
            onCancel={cancelConfirm}
            onContinue={() => runAsync(continueConfirm)}
          />
        ) : null}
        {mode === 'similar' ? (
          <ProfileUpdateSimilarSection
            match={match}
            error={error}
            busy={busy}
            onCancel={cancelConfirm}
            onUse={() => runAsync(applySimilarMatch)}
          />
        ) : null}
        {mode === 'update' ? (
          <ProfileUpdateEditSection
            question={question}
            name={name}
            setName={setName}
            error={error}
            busy={busy}
            onClose={close}
            onSave={() => runAsync(saveUpdate)}
          />
        ) : null}
      </Dialog>
    </>
  )
}
